import React from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
} from 'recharts';
import styled, { withTheme } from 'styled-components';
import { uniqueId } from 'lodash';
import Loader from '../loader/Loader';

const BarChartContainer = styled.div`
  overflow: hidden;
  width: 100%;

  @media (max-width: 870px) {
    overflow-x: auto;
  }
`;

const LoaderContainer = styled.div`
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  margin: 0 auto;

  .recharts-cartesian-axis-ticks {
    transform: translateY(8px);
    font-family: 'Roboto';
    color: black;
    font-size: 14px;
    font-weight: 500;
  }
`;

const OrigoBarChart = ({
  data,
  loading,
  theme,
  tooltip,
  barProps,
  yAxisProps,
  xAxisProps,
  tooltipProps,
  cellProps,
  maxValue,
  referenceLineProps,
  onClick,
  negativeColor,
  height,
  positiveColor,
}) => {
  const danger = negativeColor || theme.danger;
  const success = positiveColor || theme.success;
  return (
    <BarChartContainer>
      <StyledResponsiveContainer
        style={{ margin: '0 auto' }}
        height={height}
        width="90%"
        debounce={50}
      >
        {loading ? (
          <LoaderContainer>
            <Loader size={70} />
          </LoaderContainer>
        ) : (
          <BarChart style={{ margin: 'auto' }} data={data}>
            <XAxis
              allowDataOverflow={false}
              minTickGap={1}
              tickCount={2}
              tickFormatter={e => moment(e).format('DD.MM.YY')}
              tick={{ fontFamily: 'Roboto', fontSize: 14, fill: '#000' }}
              stroke="#D1D1D1"
              strokeWidth="1px"
              shapeRendering="crispEdges"
              {...xAxisProps}
            />
            <YAxis
              hide
              yAxisId="left"
              domain={[maxValue * -1 || 'dataMin', maxValue || 'dataMax']}
              {...yAxisProps}
            />
            <ReferenceLine
              y={0}
              ifOverflow="extendDomain"
              yAxisId="left"
              stroke="#000"
              {...referenceLineProps}
            />
            <Bar yAxisId="left" type="monotone" minPointSize={4} {...barProps}>
              {data.map(item => (
                <Cell
                  onClick={() => onClick(item)}
                  key={uniqueId()}
                  fill={theme && (item[yAxisProps.dataKey] > 0 ? success : danger)}
                  {...cellProps}
                />
              ))}
            </Bar>
            {tooltip && <Tooltip content={tooltip} cursor={{ opacity: 0.4 }} {...tooltipProps} />}
          </BarChart>
        )}
      </StyledResponsiveContainer>
    </BarChartContainer>
  );
};

OrigoBarChart.propTypes = {
  barProps: PropTypes.object,
  cellProps: PropTypes.object,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  maxValue: PropTypes.number,
  theme: PropTypes.object.isRequired,
  tooltip: PropTypes.func,
  tooltipProps: PropTypes.object,
  xAxisProps: PropTypes.object.isRequired,
  yAxisProps: PropTypes.object.isRequired,
  referenceLineProps: PropTypes.object,
  negativeColor: PropTypes.string,
  positiveColor: PropTypes.string,
  onClick: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

OrigoBarChart.defaultProps = {
  barProps: {},
  cellProps: {},
  loading: false,
  maxValue: null,
  onClick: () => null,
  referenceLineProps: {},
  tooltip: null,
  tooltipProps: {},
  negativeColor: null,
  positiveColor: null,
  height: 240,
};

export default withTheme(OrigoBarChart);
